/** Longest edge after downscaling — plenty for reading a grid screenshot. */
const MAX_EDGE_PX = 1600;

/** JPEG quality for the re-encode — small files, still legible. */
const JPEG_QUALITY = 0.8;

/**
 * Encoded-size ceiling per screenshot — mirrors the API's
 * MAX_SCREENSHOT_CHARS (the server rejects, not clips, an over-size image).
 */
const MAX_SCREENSHOT_CHARS = 2 * 1024 * 1024;

/** Progressively harsher qualities when the standard encode is over size. */
const FALLBACK_JPEG_QUALITIES = [0.6, 0.4];

/**
 * Downscale an attached/pasted image so its longest edge is at most
 * {@link MAX_EDGE_PX} and re-encode it as a JPEG data URI within the API's
 * per-screenshot size budget (re-encoding at lower quality when needed).
 * Rejects (throws) for non-image files — and for images that stay over
 * budget at the harshest quality — so callers can surface a validation
 * message for that image alone.
 */
export async function imageFileToDataUri(file: File | Blob): Promise<string> {
  if (!file.type.startsWith("image/")) {
    throw new Error("Only images can be attached.");
  }
  const bitmap = await loadImage(file);
  try {
    const scale = Math.min(
      1,
      MAX_EDGE_PX / Math.max(bitmap.width, bitmap.height)
    );
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d");
    if (!context) {
      throw new Error("Could not process the image.");
    }
    // White backdrop so transparent PNG regions don't turn black in JPEG.
    // Deliberately a literal, not a ui-kit theme token: this is the JPEG's
    // missing alpha channel, not themed chrome — a dark-theme surface colour
    // would tint every attached screenshot. hsla() per
    // docs/code-guidelines/components.md ("Use hsla() for all color values").
    context.fillStyle = "hsla(0, 0%, 100%, 1)";
    context.fillRect(0, 0, width, height);
    context.drawImage(bitmap, 0, 0, width, height);
    // Size belt over the resolution braces: the downscale bounds pixels, but
    // pathological content (noise, gradients) can still encode over the
    // API's budget — degrade quality before giving up on the image.
    let dataUri = canvas.toDataURL("image/jpeg", JPEG_QUALITY);
    for (const quality of FALLBACK_JPEG_QUALITIES) {
      if (dataUri.length <= MAX_SCREENSHOT_CHARS) break;
      dataUri = canvas.toDataURL("image/jpeg", quality);
    }
    if (dataUri.length > MAX_SCREENSHOT_CHARS) {
      throw new Error(
        "That image is too large to attach, even after compression."
      );
    }
    return dataUri;
  } finally {
    if ("close" in bitmap) bitmap.close();
  }
}

/** Decode via createImageBitmap, falling back to an <img> element. */
async function loadImage(
  file: File | Blob
): Promise<ImageBitmap | HTMLImageElement> {
  if (typeof createImageBitmap === "function") {
    return createImageBitmap(file);
  }
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<HTMLImageElement>((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = () => reject(new Error("Could not decode the image."));
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}
